import SafeApiKit from '@safe-global/api-kit'
import type { Abi } from 'viem'
import { decodeFunctionData } from 'viem'

import abiVestingV2Json from 'abi/abi-vesting-v2.json'
import { SAFE_API_KEY } from 'constants/index'

import { buildSafeTransactionUrl } from './propose-batch'

const abiVestingV2 = abiVestingV2Json as Abi

export interface PendingBatch {
  safeTxHash: string
  safeWebUrl: string
  nonce: number
  entriesCount: number
  confirmations: number
  confirmationsRequired: number
}

interface GetPendingBatchesParams {
  safeAddress: `0x${string}`
  chainId: number
  vestingContractAddress: `0x${string}`
}

export const getPendingBatches = async ({
  safeAddress,
  chainId,
  vestingContractAddress,
}: GetPendingBatchesParams): Promise<PendingBatch[]> => {
  if (!SAFE_API_KEY) {
    throw new Error('Safe API key is missing. Set REACT_APP_SAFE_API_KEY in your env file.')
  }

  const apiKit = new SafeApiKit({
    chainId: BigInt(chainId),
    apiKey: SAFE_API_KEY,
  })

  const response = await apiKit.getPendingTransactions(safeAddress)
  const vestingAddressLower = vestingContractAddress.toLowerCase()

  const batches: PendingBatch[] = []

  for (const tx of response.results) {
    if (!tx.to || tx.to.toLowerCase() !== vestingAddressLower) continue
    if (!tx.data) continue

    try {
      const decoded = decodeFunctionData({
        abi: abiVestingV2,
        data: tx.data as `0x${string}`,
      })
      if (decoded.functionName !== 'addVestingEntries') continue

      const users = decoded.args?.[0] as `0x${string}`[] | undefined
      const entriesCount = users?.length ?? 0

      batches.push({
        safeTxHash: tx.safeTxHash,
        safeWebUrl: buildSafeTransactionUrl(safeAddress, tx.safeTxHash),
        nonce: Number(tx.nonce),
        entriesCount,
        confirmations: tx.confirmations?.length ?? 0,
        confirmationsRequired: tx.confirmationsRequired ?? 0,
      })
    } catch {
      continue
    }
  }

  return batches.sort((a, b) => a.nonce - b.nonce)
}
